/**
 * CardCrypto — конвертация с выводом одной операцией (Flow step="card_crypto"): доллары с
 * карты уходят на указанный клиентом TRC20-адрес как USDT по курсу 1:1 (минус процент на курс,
 * если включён в админке) минус фиксированная комиссия сети.
 *
 * Операция не мгновенная: перевод уходит в блокчейн, поэтому после подтверждения показываем
 * статус «В обработке» — клиент может закрыть экран. Если сеть не пропустила перевод, деньги
 * возвращаются на карту, а операция в истории получает статус «Не выполнено».
 * Пропсы: card, config, onDone (обновить кабинет), onBack.
 */
import { useCallback, useEffect, useState } from "react";
import { useBackButton } from "@/telegram/useMainButton";
import { Screen } from "@/components/Screen";
import { Field } from "@/components/Field";
import { MaxButton } from "@/components/MaxButton";
import { Note } from "@/components/Note";
import { ApiError, cardsApi, friendlyError, type CardConfig, type IssuedCard } from "@/lib/api";
import { validateTrc20 } from "@/lib/format";

type Props = {
  card: IssuedCard;
  config: CardConfig;
  onDone: () => void;
  onBack: () => void;
};

export function CardCrypto({ card, config, onDone, onBack }: Props) {
  const [amount, setAmount] = useState("");
  const [address, setAddress] = useState("");
  const [net, setNet] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const num = Number(amount.replace(",", "."));
  const enough = num > 0 && num <= card.balance_usd;
  const addrOk = validateTrc20(address);
  const valid = enough && addrOk && num > config.crypto_fee_usdt && !submitting;

  // «получите» считает сервер: там курс-процент и фиксированная комиссия
  useEffect(() => {
    if (!(num > 0)) {
      setNet(null);
      return;
    }
    let alive = true;
    const t = setTimeout(() => {
      cardsApi
        .quoteCrypto(num)
        .then((r) => alive && setNet(r.net_usdt))
        .catch(() => alive && setNet(null));
    }, 250);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [num]);

  const submit = useCallback(async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await cardsApi.crypto(num, address.trim());
      setSent(true);
    } catch (e) {
      if (e instanceof ApiError && e.detail === "insufficient_usd") setError("На карте недостаточно средств.");
      else if (e instanceof ApiError && e.detail === "below_fee")
        setError(`Сумма должна покрывать комиссию сети (${config.crypto_fee_usdt} USDT).`);
      else if (e instanceof ApiError && e.detail === "invalid_address") setError("Неверный адрес TRC20.");
      else if (e instanceof ApiError && e.detail === "own_address") setError("Нельзя отправить на свой же служебный адрес.");
      else setError(friendlyError(e, "Не удалось отправить USDT"));
    } finally {
      setSubmitting(false);
    }
  }, [valid, submitting, num, address, config.crypto_fee_usdt]);

  useBackButton(onBack);

  // после отправки — экран ожидания: перевод подтверждается сетью, это не мгновенно
  if (sent) {
    return (
      <Screen title="Перевод в обработке" subtitle="Обычно занимает от нескольких секунд до пары минут">
        <div className="rounded-2xl border border-white/[0.06] bg-surface-card px-4 py-5 text-center">
          <div className="text-3xl">⏳</div>
          <div className="mt-3 text-[15px] font-medium">
            {(net ?? 0).toFixed(2)} USDT уходят на кошелёк
          </div>
          <div className="mx-auto mt-1 max-w-[18rem] break-all text-[12px] text-white/45">{address.trim()}</div>
          <div className="mx-auto mt-3 max-w-[18rem] text-[13px] text-white/45">
            Экран можно закрыть — статус появится в истории операций карты. Если перевод не
            пройдёт, деньги вернутся на карту.
          </div>
        </div>
        <button
          onClick={onDone}
          className="mt-5 w-full rounded-2xl bg-brand px-4 py-3.5 text-[15px] font-semibold text-surface-base shadow-lg shadow-brand/25 active:brightness-95"
        >
          Понятно
        </button>
      </Screen>
    );
  }

  const amountErr =
    amount && !enough
      ? `Не больше баланса карты — $${card.balance_usd.toFixed(2)}`
      : amount && num > 0 && num <= config.crypto_fee_usdt
        ? `Сумма должна покрывать комиссию (${config.crypto_fee_usdt} USDT)`
        : undefined;

  return (
    <Screen title="Вывод в криптовалюту" subtitle="С карты на ваш кошелёк USDT (TRC20)">
      <div className="space-y-3">
        <Field
          label="Сумма с карты"
          type="text"
          inputMode="decimal"
          autoFocus
          placeholder="0"
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^\d.,]/g, ""))}
          suffix={
            <span className="flex items-center gap-2.5">
              <span className="text-[14px] text-white/45">USD</span>
              {/* комиссия сети удерживается из результата, поэтому «МАКС» — весь баланс карты */}
              <MaxButton
                onClick={() => setAmount(String(Math.floor(card.balance_usd * 100) / 100))}
                disabled={card.balance_usd <= 0}
              />
            </span>
          }
          error={amountErr}
          hint={!amountErr ? `Доступно на карте: $${card.balance_usd.toFixed(2)}` : undefined}
        />
        <Field
          label="Кошелёк TRC20"
          type="text"
          placeholder="T..."
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          error={address && !addrOk ? "Неверный адрес TRC20 (T и 33 символа)" : undefined}
          hint={!address || addrOk ? "Адрес сети TRON (TRC20)" : undefined}
        />
      </div>

      <Note className="mt-4">
        Курс 1:1{config.crypto_fee_pct > 0 ? ` (комиссия на курс ${config.crypto_fee_pct}%)` : ""} · комиссия сети{" "}
        {config.crypto_fee_usdt} USDT. К получению:{" "}
        <span className="font-medium text-white">{net != null ? net.toFixed(2) : "0.00"} USDT</span>.
      </Note>

      {error && (
        <Note variant="danger" className="mt-3">
          {error}
        </Note>
      )}

      <button
        onClick={submit}
        disabled={!valid}
        className="mt-5 w-full rounded-2xl bg-brand px-4 py-3.5 text-[15px] font-semibold text-surface-base shadow-lg shadow-brand/25 active:brightness-95 disabled:opacity-40"
      >
        {submitting ? "Отправляем…" : "Отправить USDT"}
      </button>
    </Screen>
  );
}
